import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiGift, FiPhone, FiUser, FiMail, FiCheck, FiClock,
  FiZap, FiDownload, FiSearch, FiInstagram, FiYoutube,
  FiFacebook, FiShare2, FiChevronRight, FiUsers, FiShield
} = FiIcons;


const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/tayyabelectricstudio',
  instagram: 'https://www.instagram.com/tayyabelectricstudio',
  youtube: 'https://www.youtube.com/@TAYYABELECTRICSTUDIO',
};

const prizes = [
  { emoji: '🎧', name: 'Premium Earphones', value: '₹599', color: 'from-purple-600 to-pink-600' },
  { emoji: '🔌', name: 'Data Cable', value: '₹199', color: 'from-blue-600 to-cyan-600' },
  { emoji: '🎵', name: 'AUX Cable', value: '₹149', color: 'from-green-600 to-teal-600' },
  { emoji: '💰', name: '10% Off ₹2000+', value: 'Discount', color: 'from-yellow-500 to-orange-500' },
  { emoji: '🔧', name: 'Free Service Visit', value: '₹500', color: 'from-red-600 to-orange-600' },
  { emoji: '🛡️', name: 'Free Annual Maintenance', value: '₹999', color: 'from-indigo-600 to-purple-600' },
];

const terms = [
  'This lucky draw is completely free to enter.',
  'One entry per person per week (verified by phone number).',
  'Winner will be announced every Sunday at 8:00 PM on our Facebook page.',
  'Winner will be contacted via WhatsApp within 24 hours of announcement.',
  'Prize must be claimed within 7 days of winning announcement.',
  'Tayyab Electric Studio reserves the right to verify participation in social tasks.',
  'Employees and family members of Tayyab Electric Studio are not eligible.',
  'By entering, you agree to be contacted on WhatsApp if you win.',
  'Tayyab Electric Studio reserves the right to change prizes without prior notice.',
  'Participants must be residents of Baramullah District, J&K.',
];

const readJsonSafe = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const airtableFetch = async (method, body = null, filter = '') => {
  const res = await fetch(`/api/admin-airtable?path=Lucky%20Draw${encodeURIComponent(filter)}`, {
    method: method === 'GET' ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify({ method, body }) } : {}),
  });
  const data = await readJsonSafe(res);
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return data;
};

const getNextSunday = () => {
  const now = new Date();
  const diff = now.getDay() === 0 ? 7 : 7 - now.getDay();
  const next = new Date(now);
  next.setDate(now.getDate() + diff);
  next.setHours(20, 0, 0, 0);
  return next;
};

const useCountdown = (targetTime) => {
  const [t, setT] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = targetTime - Date.now();
      if (diff <= 0) return setT({ d: 0, h: 0, m: 0, s: 0 });
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetTime]);
  return t;
};

const QRCode = ({ value, size = 100 }) => (
  <img
    src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=ffffff&color=000000&margin=8`}
    alt="QR" width={size} height={size} className="rounded-lg"
    crossOrigin="anonymous"
  />
);

const Ticket = React.forwardRef(({ name, phone, ticketNo, drawDate }, ref) => (
  <div ref={ref} className="relative w-full max-w-sm mx-auto rounded-3xl overflow-hidden"
    style={{ background: 'linear-gradient(135deg,#0a0a1a,#0d1b4b,#0a0a1a)', border: '2px solid rgba(99,102,241,0.5)', boxShadow: '0 0 40px rgba(99,102,241,0.2)' }}>
    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(99,102,241,0.6) 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
    <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-center">
      <div className="flex items-center justify-center space-x-2">
        <SafeIcon icon={FiZap} className="text-yellow-300 text-lg" />
        <span className="text-white font-black text-base tracking-wider">TAYYAB ELECTRIC STUDIO</span>
        <SafeIcon icon={FiZap} className="text-yellow-300 text-lg" />
      </div>
      <p className="text-blue-200 text-xs mt-0.5">Unit of ARS Trading Corporation</p>
    </div>
    <div className="relative flex items-center px-4 py-2">
      <div className="w-5 h-5 bg-black rounded-full absolute -left-2.5" />
      <div className="flex-1 border-t-2 border-dashed border-gray-600" />
      <div className="w-5 h-5 bg-black rounded-full absolute -right-2.5" />
    </div>
    <div className="relative text-center px-4 pb-1">
      <p className="text-yellow-400 text-xs font-bold tracking-widest uppercase">🎉 Weekly Lucky Draw Ticket</p>
    </div>
    <div className="relative px-6 pb-4 flex items-center justify-between">
      <div className="space-y-2 flex-1 pr-4">
        <div>
          <p className="text-gray-500 text-[9px] uppercase tracking-widest">Participant</p>
          <p className="text-white font-bold text-base">{name}</p>
        </div>
        <div>
          <p className="text-gray-500 text-[9px] uppercase tracking-widest">Phone</p>
          <p className="text-blue-300 font-semibold text-sm">{phone}</p>
        </div>
        <div>
          <p className="text-gray-500 text-[9px] uppercase tracking-widest">Draw Date</p>
          <p className="text-white font-semibold text-xs">{drawDate}</p>
        </div>
      </div>
      <div className="flex flex-col items-center">
        <QRCode value={ticketNo} size={85} />
        <p className="text-gray-500 text-[9px] mt-1">Scan to verify</p>
      </div>
    </div>
    <div className="relative flex items-center px-4 py-1">
      <div className="w-5 h-5 bg-black rounded-full absolute -left-2.5" />
      <div className="flex-1 border-t-2 border-dashed border-gray-600" />
      <div className="w-5 h-5 bg-black rounded-full absolute -right-2.5" />
    </div>
    <div className="relative px-6 py-4 text-center">
      <p className="text-gray-500 text-[9px] uppercase tracking-widest mb-1">Ticket Number</p>
      <p className="text-2xl font-black tracking-widest text-yellow-400">{ticketNo}</p>
      <p className="text-gray-600 text-[9px] mt-1">tayyabelectric.com • +91 99066 69918</p>
    </div>
  </div>
));

const StatusChecker = () => {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('ticket');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const check = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const field = type === 'ticket' ? 'Ticket No' : 'Phone';
      const data = await airtableFetch('GET', null, `?filterByFormula={${field}}="${query.trim()}"`);
      if (data.records?.length > 0) setResult(data.records[0].fields);
      else setError('No entry found. Please check your details.');
    } catch { setError('Could not check. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-gray-900/60 border border-gray-700/50 rounded-3xl p-6">
      <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
        <SafeIcon icon={FiSearch} className="text-blue-400" />
        <span>Check Your Status</span>
      </h3>
      <div className="flex bg-gray-800 rounded-xl p-1 mb-4">
        {['ticket', 'phone'].map(t => (
          <button key={t} onClick={() => setType(t)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${type === t ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>
            {t === 'ticket' ? '🎫 Ticket No' : '📞 Phone No'}
          </button>
        ))}
      </div>
      <div className="flex space-x-2 mb-4">
        <input type="text" placeholder={type === 'ticket' ? 'e.g. TES-LD-12345' : 'Your phone number'}
          value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && check()}
          className="flex-1 bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none"
        />
        <button onClick={check} disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl disabled:opacity-50">
          <SafeIcon icon={FiSearch} />
        </button>
      </div>
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/60 border border-green-500/30 rounded-2xl p-4 space-y-2">
          <div className="flex items-center space-x-2 mb-2">
            <div className={`w-3 h-3 rounded-full ${result.Status === 'Winner' ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
            <span className={`font-bold text-sm ${result.Status === 'Winner' ? 'text-yellow-400' : 'text-green-400'}`}>
              {result.Status === 'Winner' ? '🏆 YOU WON!' : '✅ Entry Confirmed'}
            </span>
          </div>
          <p className="text-white font-semibold">{result.Name}</p>
          <p className="text-gray-400 text-sm">🎫 {result['Ticket No']}</p>
          <p className="text-gray-400 text-sm">📅 Draw: {result['Draw Date']}</p>
          {result.Prize && <p className="text-yellow-400 text-sm font-bold">🎁 Prize: {result.Prize}</p>}
        </motion.div>
      )}
    </div>
  );
};

const LuckyDraw = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [tasks, setTasks] = useState({ facebook: false, instagram: false, youtube: false, whatsapp: false, groups: false });
  const [ticketNo, setTicketNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [count, setCount] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const ticketRef = useRef(null);

  // ✅ Stable timestamp — prevents infinite re-render
  const nextDrawTimeRef = useRef(getNextSunday().getTime());
  const nextDraw = new Date(nextDrawTimeRef.current);
  const countdown = useCountdown(nextDrawTimeRef.current);
  const allTasksDone = Object.values(tasks).every(Boolean);

  useEffect(() => {
    let intervalId = null;
    let failed = false;
    const load = async () => {
      if (failed) return;
      try {
        const d = await airtableFetch('GET', null, '?fields[]=Ticket+No');
        setCount(d.records?.length || 0);
      } catch (e) {
        if (e.message.includes('403') || e.message.includes('404')) {
          failed = true;
          if (intervalId) clearInterval(intervalId);
        }
      }
    };
    load();
    intervalId = setInterval(load, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // ✅ FIXED handleSubmit — uses Netlify function, duplicate check, GA4 tracking
  const handleSubmit = async () => {
    if (!allTasksDone) return setError('Please complete all tasks first!');
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/lucky-draw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
        }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok) {
        setError(data.error || 'Could not submit. Please try again.');
        setLoading(false);
        return;
      }
      setTicketNo(data.ticketNo);
      setCount(c => c + 1);
      setStep(4);
      // GA4 tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'lucky_draw_entry', { event_category: 'lucky_draw' });
      }
      // WhatsApp confirmation
      setTimeout(() => {
        const msg = `🎉 Lucky Draw Entry Confirmed!\n\n🎫 Ticket: ${data.ticketNo}\n👤 Name: ${form.name}\n📅 Draw: ${data.drawDate}\n\nCheck status: https://arstc.netlify.app/lucky-draw\n\nGood Luck! — Tayyab Electric Studio ⚡`;
        window.open(`https://wa.me/919906669918?text=${encodeURIComponent(msg)}`, '_blank');
      }, 2000);
    } catch { setError('Could not submit. Please try again.'); }
    finally { setLoading(false); }
  };

  const downloadTicket = async () => {
    try {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = () => {
        window.html2canvas(ticketRef.current, { scale: 3, backgroundColor: '#0a0a1a', useCORS: true }).then(canvas => {
          const a = document.createElement('a');
          a.download = `TES-Lucky-Draw-${ticketNo}.png`;
          a.href = canvas.toDataURL('image/png');
          a.click();
        });
      };
      document.body.appendChild(script);
    } catch { alert('Please try again in a moment.'); }
  };

  return (
    // ✅ FIXED spacing — removed marginTop: '-280px' hack, using clean paddingTop
    <div className="min-h-screen bg-black" style={{ paddingTop: '80px' }}>

      {/* Hero */}
      <section className="py-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-black to-blue-900/20" />
        {['🎧','🔌','🎵','💰','🔧','🎁','⭐','🎊'].map((e, i) => (
          <motion.div key={i} className="absolute text-xl pointer-events-none select-none"
            style={{ left: `${8 + i * 11}%`, top: `${8 + (i % 3) * 28}%` }}
            animate={{ y: [0, -18, 0], opacity: [0.2, 0.7, 0.2] }}
            transition={{ duration: 3 + i * 0.4, repeat: Infinity, delay: i * 0.3 }}
          >{e}</motion.div>
        ))}
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="inline-flex items-center space-x-2 bg-green-600/20 border border-green-500/30 rounded-full px-4 py-1.5 text-green-300 text-sm mb-4">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <SafeIcon icon={FiUsers} className="text-sm" />
            <span><b>{count}</b> participants entered this week</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-6xl font-black text-white mb-3 leading-tight">
            🎉 Weekly
            <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">Lucky Draw</span>
          </motion.h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto mb-8">
            Enter for FREE every week and win amazing prizes!
          </p>
          <div className="inline-flex items-center space-x-3 bg-gray-900/60 border border-purple-500/30 rounded-2xl px-5 py-3 mb-6">
            <SafeIcon icon={FiClock} className="text-purple-400" />
            <div className="flex items-center space-x-2">
              {[{ val: countdown.d, l: 'Days' }, { val: countdown.h, l: 'Hrs' }, { val: countdown.m, l: 'Min' }, { val: countdown.s, l: 'Sec' }].map((t, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-purple-400 font-bold">:</span>}
                  <div className="text-center min-w-[32px]">
                    <div className="text-xl font-black text-white">{String(t.val ?? 0).padStart(2, '0')}</div>
                    <div className="text-gray-500 text-[10px]">{t.l}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <span className="text-gray-500 text-xs">Next Draw</span>
          </div>
          {step === 0 && (
            <div>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                onClick={() => setStep(1)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black px-10 py-4 rounded-2xl text-lg shadow-lg shadow-purple-500/30">
                🎰 Enter Lucky Draw — FREE!
              </motion.button>
              <p className="text-gray-600 text-xs mt-3">No purchase required • Open to Baramulla residents</p>
            </div>
          )}
        </div>
      </section>

      {/* Prizes */}
      <section className="py-8 bg-gray-900/20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-white text-center mb-6">🏆 This Week's Prizes</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {prizes.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className="bg-gray-900/60 border border-gray-700/50 rounded-2xl p-3 text-center hover:scale-105 transition-transform">
                <div className={`w-10 h-10 bg-gradient-to-br ${p.color} rounded-xl flex items-center justify-center mx-auto mb-2 text-xl`}>{p.emoji}</div>
                <p className="text-white text-[10px] font-bold leading-tight">{p.name}</p>
                <p className="text-gray-500 text-[10px]">{p.value}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 bg-black">
        <div className="max-w-lg mx-auto px-4">
          <AnimatePresence mode="wait">

            {/* STEP 1 — Terms */}
            {step === 1 && (
              <motion.div key="terms" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                className="bg-gray-900/60 border border-gray-700 rounded-3xl p-6">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-white font-black text-lg">Terms & Conditions</h2>
                    <p className="text-gray-500 text-xs">Please read before entering</p>
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-2xl p-4 mb-5 max-h-56 overflow-y-auto space-y-2">
                  {terms.map((t, i) => (
                    <div key={i} className="flex items-start space-x-2">
                      <span className="text-blue-400 font-bold text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                      <p className="text-gray-300 text-xs leading-relaxed">{t}</p>
                    </div>
                  ))}
                </div>
                <div onClick={() => setTermsAccepted(!termsAccepted)} className="flex items-center space-x-3 cursor-pointer mb-5">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${termsAccepted ? 'bg-blue-600 border-blue-600' : 'border-gray-600'}`}>
                    {termsAccepted && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
                  </div>
                  <p className="text-gray-300 text-sm">I agree to the Terms & Conditions</p>
                </div>
                <button onClick={() => termsAccepted && setStep(2)} disabled={!termsAccepted}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-3 rounded-2xl flex items-center justify-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed">
                  <span>Continue</span><SafeIcon icon={FiChevronRight} />
                </button>
              </motion.div>
            )}

            {/* STEP 2 — Form */}
            {step === 2 && (
              <motion.div key="form" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                className="bg-gray-900/60 border border-gray-700 rounded-3xl p-6">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="text-3xl">📝</div>
                  <div>
                    <h2 className="text-white font-black text-lg">Your Details</h2>
                    <p className="text-gray-500 text-xs">Step 1 of 2</p>
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm mb-3 bg-red-900/20 border border-red-500/30 rounded-xl p-3">{error}</p>}
                <div className="space-y-3 mb-5">
                  {[
                    { icon: FiUser, key: 'name', placeholder: 'Full Name', type: 'text' },
                    { icon: FiPhone, key: 'phone', placeholder: 'WhatsApp Number', type: 'tel' },
                    { icon: FiMail, key: 'email', placeholder: 'Email (optional)', type: 'email' },
                  ].map(f => (
                    <div key={f.key} className="relative">
                      <SafeIcon icon={f.icon} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input type={f.type} placeholder={f.placeholder} value={form[f.key]}
                        onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-11 pr-4 py-3 text-white text-sm focus:border-purple-500 focus:outline-none transition-colors"
                      />
                    </div>
                  ))}
                </div>
                <button onClick={() => { if (!form.name || !form.phone) return setError('Please fill name and phone.'); setError(''); setStep(3); }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 rounded-2xl flex items-center justify-center space-x-2">
                  <span>Next — Complete Tasks</span><SafeIcon icon={FiChevronRight} />
                </button>
              </motion.div>
            )}

            {/* STEP 3 — Tasks */}
            {step === 3 && (
              <motion.div key="tasks" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                className="bg-gray-900/60 border border-gray-700 rounded-3xl p-6">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="text-3xl">📱</div>
                  <div>
                    <h2 className="text-white font-black text-lg">Complete Tasks</h2>
                    <p className="text-gray-500 text-xs">Step 2 of 2 — Complete all 5 tasks</p>
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm mb-3 bg-red-900/20 border border-red-500/30 rounded-xl p-3">{error}</p>}
                <div className="space-y-3 mb-4">
                  {[
                    { key: 'facebook', icon: FiFacebook, label: 'Follow on Facebook', color: 'bg-blue-600', link: SOCIAL_LINKS.facebook, action: 'Follow' },
                    { key: 'instagram', icon: FiInstagram, label: 'Follow on Instagram', color: 'bg-pink-600', link: SOCIAL_LINKS.instagram, action: 'Follow' },
                    { key: 'youtube', icon: FiYoutube, label: 'Subscribe on YouTube', color: 'bg-red-600', link: SOCIAL_LINKS.youtube, action: 'Subscribe' },
                    { key: 'whatsapp', icon: FiShare2, label: 'Share with 5 Friends', color: 'bg-green-600', link: `https://wa.me/?text=${encodeURIComponent('🎉 Enter Tayyab Electric Studio Weekly Lucky Draw!\nhttps://arstc.netlify.app/lucky-draw')}`, action: 'Share' },
                    { key: 'groups', icon: FiUsers, label: 'Share in Groups', color: 'bg-teal-600', link: `https://wa.me/?text=${encodeURIComponent('🎰 FREE Lucky Draw by Tayyab Electric Studio!\nhttps://arstc.netlify.app/lucky-draw')}`, action: 'Share' },
                  ].map(task => (
                    <div key={task.key} className={`flex items-center justify-between p-3 rounded-2xl border transition-all ${tasks[task.key] ? 'bg-green-900/20 border-green-500/40' : 'bg-gray-800/40 border-gray-700/50'}`}>
                      <div className="flex items-center space-x-3">
                        <div className={`w-9 h-9 ${task.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <SafeIcon icon={task.icon} className="text-white text-sm" />
                        </div>
                        <p className={`text-sm font-medium ${tasks[task.key] ? 'text-green-300' : 'text-white'}`}>{task.label}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <a href={task.link} target="_blank" rel="noopener noreferrer"
                          className="text-blue-400 text-xs underline"
                          onClick={() => setTimeout(() => setTasks(t => ({ ...t, [task.key]: true })), 1500)}>
                          {task.action}
                        </a>
                        <div onClick={() => setTasks(t => ({ ...t, [task.key]: !t[task.key] }))}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${tasks[task.key] ? 'bg-green-500 border-green-500' : 'border-gray-600'}`}>
                          {tasks[task.key] && <SafeIcon icon={FiCheck} className="text-white text-xs" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-800/40 rounded-xl p-3 mb-4 flex items-center space-x-2">
                  <span className="text-white text-sm font-bold">{Object.values(tasks).filter(Boolean).length}/5</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div className="bg-gradient-to-r from-purple-500 to-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(Object.values(tasks).filter(Boolean).length / 5) * 100}%` }} />
                  </div>
                  <span className="text-gray-400 text-xs">tasks done</span>
                </div>
                <button onClick={handleSubmit} disabled={!allTasksDone || loading}
                  className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold py-3 rounded-2xl flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                  {loading ? 'Generating Ticket...' : '🎫 Get My Ticket!'}
                </button>
              </motion.div>
            )}

            {/* STEP 4 — Ticket */}
            {step === 4 && (
              <motion.div key="ticket" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="space-y-5">
                <div className="text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
                    className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 border-2 border-green-500/40">
                    <SafeIcon icon={FiCheck} className="text-3xl text-green-400" />
                  </motion.div>
                  <h2 className="text-2xl font-black text-white mb-1">You're In! 🎉</h2>
                  <p className="text-gray-400 text-sm">Your lucky draw ticket is ready!</p>
                </div>
                <Ticket ref={ticketRef} name={form.name} phone={form.phone} ticketNo={ticketNo} drawDate={nextDraw.toDateString()} />
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={downloadTicket}
                    className="flex items-center justify-center space-x-2 bg-blue-600/20 border border-blue-500/40 text-blue-300 py-3 rounded-2xl text-sm font-medium hover:bg-blue-600/30 transition-colors">
                    <SafeIcon icon={FiDownload} /><span>Download</span>
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`🎫 I entered TES Lucky Draw!\nTicket: ${ticketNo}\nhttps://arstc.netlify.app/lucky-draw`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 bg-green-600/20 border border-green-500/40 text-green-300 py-3 rounded-2xl text-sm font-medium hover:bg-green-600/30 transition-colors">
                    <SafeIcon icon={FiShare2} /><span>Share</span>
                  </a>
                </div>
                <div className="bg-gray-900/60 border border-gray-700 rounded-2xl p-4 text-sm space-y-1">
                  <p className="text-gray-400">📅 Draw: <span className="text-white">Every Sunday at 8:00 PM</span></p>
                  <p className="text-gray-400">📢 Announced: <span className="text-white">Facebook page</span></p>
                  <p className="text-gray-400">📞 Winner contacted: <span className="text-white">WhatsApp</span></p>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </section>

      {/* Status Checker */}
      <section className="py-12 bg-gray-900/20">
        <div className="max-w-lg mx-auto px-4">
          <StatusChecker />
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 bg-black">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-8">How It Works</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { step: '1', icon: '📝', title: 'Fill Form', desc: 'Enter your name & phone' },
              { step: '2', icon: '📱', title: 'Complete Tasks', desc: 'Follow & share our pages' },
              { step: '3', icon: '🎫', title: 'Get Ticket', desc: 'Download your lucky ticket' },
              { step: '4', icon: '🏆', title: 'Win!', desc: 'Winner announced Sunday 8 PM' },
            ].map((s, i) => (
              <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-2xl p-4 text-center">
                <div className="text-3xl mb-2">{s.icon}</div>
                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold mx-auto mb-2">{s.step}</div>
                <h3 className="text-white font-bold text-sm mb-1">{s.title}</h3>
                <p className="text-gray-400 text-xs">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LuckyDraw;
